/// <reference types="office-js" />

import {
  Badge,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  MessageBar,
  MessageBarBody,
  Spinner,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
} from "@fluentui/react-components";
import React, { useEffect, useMemo, useState } from "react";
import {
  Add24Regular,
  ArrowLeft24Regular,
  Briefcase24Regular,
  ChevronDown16Regular,
  ChevronUp16Regular,
  Clock16Regular,
  DocumentTable24Regular,
} from "@fluentui/react-icons";
import { Page } from "../components/App";
import { useJob } from "../context/JobContext";
import { getJobStatus, listJobs } from "../services/jobService";
import { useMsal } from "@azure/msal-react";
import { getAccessToken } from "../services/authService";
import { JobStatus } from "../types";
import { downloadFileBase64ByIds } from "../services/fileService";
import { taskPaneBackground } from "../theme";
import { shouldActivateJobCard } from "./dashboardInteraction";

interface JobDetails {
  status: JobStatus;
  final_report_url?: string;
  final_report_json_url?: string;
  final_report_drive_id?: string;
  final_report_item_id?: string;
  final_json_drive_id?: string;
  final_json_item_id?: string;
  error_message?: string;
  status_message?: string;
  progress_percent?: number;
  rejected_file_count?: number;
  rejected_files?: RejectedFileInfo[];
}

interface RejectedFileInfo {
  file_name: string;
  reason: string;
}

interface DashboardJob {
  id: string;
  job_type?: string;
  status?: JobStatus | string;
  created_at?: string;
  updated_at?: string;
  progress_percent?: number;
  final_report_drive_id?: string;
  final_report_item_id?: string;
  error_message?: string;
  rejected_file_count?: number;
  rejected_files?: RejectedFileInfo[];
}

type JobTheme = "active" | "completed" | "failed";

const getJobTheme = (status: JobStatus | string): JobTheme => {
  if (status === "completed") {
    return "completed";
  }

  if (status === "failed") {
    return "failed";
  }

  return "active";
};

const useStyles = makeStyles({
  root: {
    boxSizing: "border-box",
    display: "flex",
    height: "87vh",
    flexDirection: "column",
    ...shorthands.gap("12px"),
    ...shorthands.padding("16px"),
    overflow: "hidden",
    color: tokens.colorNeutralForeground1,
    ...taskPaneBackground,
    "@media (max-width: 480px)": {
      ...shorthands.gap("8px"),
      ...shorthands.padding("12px"),
    },
  },
  surface: {
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    ...shorthands.borderRadius("14px"),
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow4,
  },
  headerCard: {
    position: "relative",
    flexShrink: 0,
    overflow: "hidden",
    ...shorthands.padding("20px"),
    "@media (max-width: 480px)": {
      ...shorthands.padding("12px"),
    },
  },
  headerRow: {
    display: "flex",
    alignItems: "flex-start",
    ...shorthands.gap("12px"),
    paddingTop: "4px",
    "@media (max-width: 480px)": {
      ...shorthands.gap("8px"),
      paddingTop: 0,
    },
  },
  headerIcon: {
    display: "flex",
    width: "40px",
    height: "40px",
    flexShrink: 0,
    alignItems: "center",
    justifyContent: "center",
    color: "#0e8e0e",
    ...shorthands.border("1px", "solid", "#8bd68b"),
    ...shorthands.borderRadius("12px"),
    backgroundColor: "#f0fff0",
    "@media (max-width: 480px)": {
      width: "34px",
      height: "34px",
      ...shorthands.borderRadius("10px"),
    },
  },
  eyebrow: {
    margin: 0,
    color: "#0e8e0e",
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightBold,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
  },
  pageTitle: {
    marginTop: "2px",
    marginBottom: 0,
    fontSize: "22px",
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: "1.2",
    letterSpacing: "-0.025em",
    "@media (max-width: 480px)": {
      fontSize: "20px",
    },
  },
  pageDescription: {
    marginTop: "4px",
    marginBottom: 0,
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase300,
    lineHeight: "20px",
    "@media (max-width: 480px)": {
      marginTop: "2px",
      lineHeight: "18px",
    },
  },
  errorMessage: {
    marginTop: "16px",
    ...shorthands.border("1px", "solid", "#fecdd3"),
    ...shorthands.borderRadius("10px"),
    backgroundColor: "#fff1f2",
  },
  listContainer: {
    display: "flex",
    minHeight: 0,
    flex: 1,
    flexDirection: "column",
  },
  jobsCard: {
    display: "flex",
    minHeight: 0,
    flex: 1,
    flexDirection: "column",
    ...shorthands.padding("16px"),
    "@media (max-width: 480px)": {
      ...shorthands.padding("10px"),
    },
  },
  listHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    ...shorthands.gap("12px"),
    ...shorthands.borderBottom("1px", "solid", tokens.colorNeutralStroke3),
    paddingBottom: "12px",
    "@media (max-width: 480px)": {
      paddingBottom: "8px",
    },
  },
  listTitle: {
    margin: 0,
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
  },
  listDescription: {
    marginTop: "2px",
    marginBottom: 0,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  totalBadge: {
    color: tokens.colorNeutralForeground3,
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.borderColor(tokens.colorNeutralStroke2),
  },
  jobsScrollArea: {
    display: "flex",
    minHeight: 0,
    flex: 1,
    flexDirection: "column",
    ...shorthands.gap("12px"),
    marginTop: "12px",
    paddingRight: "4px",
    overflowY: "auto",
    "@media (max-width: 480px)": {
      ...shorthands.gap("4px"),
      marginTop: "4px",
    },
  },
  jobCard: {
    position: "relative",
    flexShrink: 0,
    overflow: "hidden",
    cursor: "pointer",
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    ...shorthands.borderRadius("12px"),
    ...shorthands.padding("16px"),
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow4,
    transitionProperty: "border-color, box-shadow, transform",
    transitionDuration: "150ms",
    ":hover": {
      ...shorthands.borderColor(tokens.colorNeutralStroke1),
      boxShadow: tokens.shadow8,
      transform: "translateY(-2px)",
    },
    ":focus-visible": {
      outlineColor: "#3b82f6",
      outlineStyle: "solid",
      outlineWidth: "2px",
      outlineOffset: "2px",
    },
    "@media (max-width: 480px)": {
      ...shorthands.padding("5px", "10px"),
    },
  },
  jobCardActive: {
    ...shorthands.borderColor("#93c5fd"),
    boxShadow: "0 0 0 1px #dbeafe",
  },
  activeProgress: { backgroundColor: "#3b82f6" },
  completedProgress: { backgroundColor: "#0e8e0e" },
  failedProgress: { backgroundColor: "#f43f5e" },
  jobHeader: {
    display: "flex",
    minWidth: 0,
    flexWrap: "wrap",
    alignItems: "flex-start",
    justifyContent: "space-between",
    ...shorthands.gap("8px"),
    marginBottom: "12px",
    paddingTop: "4px",
    "@media (max-width: 480px)": {
      marginBottom: "2px",
      paddingTop: 0,
    },
  },
  jobHeading: { minWidth: 0, flex: 1 },
  jobTitle: {
    margin: 0,
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    textTransform: "capitalize",
    overflowWrap: "anywhere",
  },
  jobId: {
    marginTop: "2px",
    marginBottom: 0,
    overflow: "hidden",
    color: tokens.colorNeutralForeground4,
    fontSize: tokens.fontSizeBase100,
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  statusBadge: { flexShrink: 0 },
  activeBadge: {
    color: "#1d4ed8",
    backgroundColor: "#eff6ff",
    ...shorthands.borderColor("#bfdbfe"),
  },
  completedBadge: {
    color: "#047857",
    backgroundColor: "#ecfdf5",
    ...shorthands.borderColor("#0e8e0e"),
    ...shorthands.borderRadius("8px"),
  },
  failedBadge: {
    color: "#be123c",
    backgroundColor: "#fff1f2",
    ...shorthands.borderColor("#fecdd3"),
  },
  jobBody: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap("12px"),
    "@media (max-width: 480px)": {
      ...shorthands.gap("4px"),
    },
  },
  submittedRow: {
    display: "flex",
    alignItems: "center",
    minWidth: 0,
    ...shorthands.gap("4px"),
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    whiteSpace: "nowrap",
    overflow: "hidden",
  },
  submittedTime: {
    "@media (max-width: 380px)": {
      display: "none",
    },
  },
  warningRow: {
    display: "flex",
    minWidth: 0,
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    ...shorthands.gap("8px"),
    ...shorthands.border("1px", "solid", "#fde68a"),
    ...shorthands.borderRadius("8px"),
    ...shorthands.padding("8px", "12px"),
    color: "#92400e",
    backgroundColor: "#fffbeb",
  },
  warningText: { minWidth: 0, flex: 1, fontSize: tokens.fontSizeBase200 },
  warningButton: { minWidth: "fit-content", color: "#92400e" },
  rejectedList: {
    maxHeight: "96px",
    overflowY: "auto",
    ...shorthands.border("1px", "solid", "#fef3c7"),
    ...shorthands.borderLeft("4px", "solid", "#fbbf24"),
    ...shorthands.borderRadius("8px"),
    ...shorthands.padding("8px", "12px"),
    backgroundColor: "#fffbeb",
  },
  rejectedItem: {
    margin: 0,
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    lineHeight: "20px",
    overflowWrap: "anywhere",
  },
  progressTrack: {
    width: "100%",
    height: "6px",
    overflow: "hidden",
    ...shorthands.borderRadius("999px"),
    backgroundColor: tokens.colorNeutralBackground3,
  },
  progressBar: {
    height: "100%",
    ...shorthands.borderRadius("999px"),
    transitionProperty: "width",
    transitionDuration: "300ms",
  },
  jobActions: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto",
    alignItems: "center",
    ...shorthands.gap("8px"),
    marginTop: "8px",
    "@media (max-width: 480px)": {
      marginTop: "2px",
    },
  },
  actionMeta: {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap("4px"),
  },
  errorText: {
    minWidth: 0,
    margin: 0,
    color: "#be123c",
    fontSize: tokens.fontSizeBase200,
    lineHeight: "20px",
    overflowWrap: "anywhere",
  },
  loadingState: {
    display: "flex",
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    ...shorthands.padding("40px", "0"),
  },
  emptyState: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    ...shorthands.padding("40px", "20px"),
    textAlign: "center",
  },
  emptyIcon: {
    display: "flex",
    width: "44px",
    height: "44px",
    alignItems: "center",
    justifyContent: "center",
    color: tokens.colorNeutralForeground4,
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    ...shorthands.borderRadius("12px"),
    backgroundColor: tokens.colorNeutralBackground2,
  },
  emptyTitle: {
    marginTop: "12px",
    marginBottom: 0,
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
  },
  emptyDescription: {
    marginTop: "4px",
    marginBottom: 0,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    lineHeight: "20px",
  },
  viewMoreRow: { display: "flex", justifyContent: "center", paddingTop: "4px" },
  secondaryButton: {
    color: "#1e40af",
    backgroundColor: "#eff6ff",
    ...shorthands.border("1px", "solid", "#bfdbfe"),
    boxShadow: "none",
    ":hover": { color: "#1e3a8a", backgroundColor: "#dbeafe" },
  },
  footerCard: {
    flexShrink: 0,
    ...shorthands.padding("12px"),
    "@media (max-width: 480px)": {
      ...shorthands.padding("8px"),
    },
  },
  footerActions: { display: "flex", ...shorthands.gap("8px") },
  footerButton: { minWidth: 0, flex: 1 },
  backButton: {
    color: tokens.colorNeutralForeground2,
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    boxShadow: "none",
    ":hover": { backgroundColor: tokens.colorNeutralBackground2 },
  },
  dialogSurface: {
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    ...shorthands.borderRadius("14px"),
    boxShadow: tokens.shadow64,
  },
  dialogTitle: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
  },
  dialogContent: { color: tokens.colorNeutralForeground2, lineHeight: "24px" },
});

const WORKBOOK_SETTING_JOB_ID = "SS_JOB_ID";
const WORKBOOK_SETTING_SHEETS = "SS_SHEETS";
const TEMP_SHEET_NAME = "__SS_TEMP__";
const META_SHEET_NAME = "_SS_META";
const ST_SHEET_PREFIX = "_ST_";
const JOB_SHEET_SUFFIX_LENGTH = 6;
const MAX_SHEET_NAME_LENGTH = 31;

interface WorkbookSnapshot {
  currentJobId: string | null;
  managedSheetNames: string[];
  sheetNames: string[];
}

interface CompletedJobSummary {
  id: string;
  job_type?: string;
  final_report_drive_id?: string;
  final_report_item_id?: string;
}

interface PendingReloadState {
  job: CompletedJobSummary;
  base64Loader: () => Promise<string>;
  snapshot?: WorkbookSnapshot;
  onLoaded?: () => void;
}

// helper: validate the workbook payload before we hand it to Office.js.
// Note: XLSX files are ZIP containers, so the Base64 typically starts with "UEsDB" (PK..).
const validateWorkbookBase64 = (base64: string): void => {
  if (!base64 || typeof base64 !== "string") {
    throw new Error("Report download returned an empty workbook payload.");
  }
  const trimmed = base64.trim();
  if (trimmed.length < 20) {
    throw new Error("Report payload is too small to be a valid XLSX workbook.");
  }
  // Best-effort check: avoid false positives from whitespace or data-URI prefixes.
  const normalized = trimmed.replace(/^data:.*;base64,/, "");
  if (!normalized.startsWith("UEsDB")) {
    // We don't hard-fail on this because some encoders produce different ZIP header variants,
    // but we *do* want a clearer clue than Office.js' generic "invalid argument" error.

    console.warn(
      "Downloaded report base64 does not look like an XLSX ZIP payload (missing UEsDB prefix)."
    );
  }
};

// helper: persist workbook-level metadata using settings as a cross-platform fallback.
const writeWorkbookSettingString = async (
  context: Excel.RequestContext,
  workbook: Excel.Workbook,
  key: string,
  value: string,
  label: string
): Promise<boolean> => {
  try {
    const setting = workbook.settings.getItemOrNullObject(key);
    setting.load("key");
    await context.sync();

    if ((setting as any).isNullObject) {
      workbook.settings.add(key, value);
    } else {
      setting.value = value;
    }
    await context.sync();
    return true;
  } catch (settingError: any) {
    console.warn(
      "[SS_META persist] Failed storing %s in workbook.settings key=%s code=%s message=%s",
      label,
      key,
      settingError?.code ?? "unknown",
      settingError?.message ?? String(settingError)
    );
    return false;
  }
};

// helper: parse sheet names from workbook.settings payload.
const parseSheetNamesSetting = (value: unknown): string[] => {
  if (typeof value !== "string" || !value.trim()) {
    return [];
  }
  try {
    const parsed = JSON.parse(value);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter(
      (entry): entry is string => typeof entry === "string" && entry.trim().length > 0
    );
  } catch {
    // Backward compatibility if older data used pipe-separated storage.
    return splitManagedSheetString(value);
  }
};

// helper: write report metadata to workbook settings as the canonical store.
const persistWorkbookMetadata = async (
  context: Excel.RequestContext,
  workbook: Excel.Workbook,
  jobId: string,
  sheetNames: string[]
): Promise<void> => {
  const sheetsJson = JSON.stringify(sheetNames);
  const jobIdSaved = await writeWorkbookSettingString(
    context,
    workbook,
    WORKBOOK_SETTING_JOB_ID,
    jobId,
    "jobId"
  );
  const sheetsSaved = await writeWorkbookSettingString(
    context,
    workbook,
    WORKBOOK_SETTING_SHEETS,
    sheetsJson,
    "sheets"
  );

  if (!jobIdSaved || !sheetsSaved) {
    throw new Error("Unable to persist workbook settings metadata.");
  }

  console.log(
    "[SS_META persist] Metadata persisted in workbook settings. sheetCount=%d",
    sheetNames.length
  );
};

// helper: split the stored sheet list back into individual worksheet names.
const splitManagedSheetString = (value: string | null): string[] => {
  if (!value) {
    return [];
  }
  return value
    .split("|")
    .map((name) => name.trim())
    .filter((name) => name.length > 0);
};

// helper: treat sheets prefixed with `_SS_` as technical sheets that we should not rename.
const shouldPreserveSheetName = (name: string): boolean => {
  return name.startsWith("_SS_");
};

// helper: craft a unique worksheet name that appends a job-id suffix while staying inside Excel's 31 character limit.
const buildSheetNameWithJobId = (
  baseName: string,
  jobId: string,
  reserved: Set<string>
): string => {
  const shortId = jobId.slice(0, JOB_SHEET_SUFFIX_LENGTH).toUpperCase(); // keep suffix concise per instruction.
  const suffix = ` (job ${shortId})`; // consistent suffix for traceability.
  // We prefix report sheets with `_ST_` so we can identify and clean them up reliably even if metadata is missing.
  const baseLimit = Math.max(1, MAX_SHEET_NAME_LENGTH - suffix.length - ST_SHEET_PREFIX.length); // ensure we always leave room for prefix + suffix.
  const sanitizedBase = baseName.slice(0, baseLimit).trim() || "Sheet"; // fall back to a generic sheet label if empty.

  let candidate = `${ST_SHEET_PREFIX}${sanitizedBase}${suffix}`;
  let counter = 2; // iterate if collisions occur.
  while (reserved.has(candidate)) {
    const counterTag = `-${counter}`;
    const allowedBase = Math.max(1, baseLimit - counterTag.length);
    const adjustedBase = sanitizedBase.slice(0, allowedBase) || "Sheet";
    candidate = `${ST_SHEET_PREFIX}${adjustedBase}${counterTag}${suffix}`;
    counter += 1;
  }
  reserved.add(candidate); // reserve so subsequent checks avoid duplicates.
  return candidate;
};

// helper: capture current workbook metadata so we can decide whether to prompt for reloads or cleanup.
const readWorkbookSnapshot = async (): Promise<WorkbookSnapshot> => {
  return Excel.run(async (context) => {
    const workbook = context.workbook; // Excel workbook proxy.
    const sheets = workbook.worksheets; // collection of worksheets.
    sheets.load("items/name"); // load names to compare before/after.

    const jobIdSetting = workbook.settings.getItemOrNullObject(WORKBOOK_SETTING_JOB_ID);
    const sheetsSetting = workbook.settings.getItemOrNullObject(WORKBOOK_SETTING_SHEETS);
    jobIdSetting.load("value");
    sheetsSetting.load("value");

    await context.sync(); // fetch metadata from Excel runtime.

    const sheetNames = sheets.items.map((sheet) => sheet.name); // snapshot current worksheet names.
    const currentJobId =
      (jobIdSetting as any).isNullObject || typeof jobIdSetting.value !== "string"
        ? null
        : jobIdSetting.value;
    const managedFromSettings = (sheetsSetting as any).isNullObject
      ? []
      : parseSheetNamesSetting(sheetsSetting.value);
    const managedSheetNames = managedFromSettings;

    return { currentJobId, managedSheetNames, sheetNames }; // return shape consumed by the caller.
  });
};

// helper: apply a new report into the workbook, cleaning prior managed sheets and stamping metadata for future reloads.
const applyReportToWorkbook = async (
  base64: string,
  job: CompletedJobSummary,
  snapshot: WorkbookSnapshot
): Promise<void> => {
  // Validate early, before we enter Excel.run, so errors are actionable.
  validateWorkbookBase64(base64);

  // Phase 1 (must succeed): insert the workbook from Base64.
  // If *this* fails, the report did not load and we should surface a clear error.
  const insertResult = await Excel.run(async (context) => {
    const workbook = context.workbook; // active workbook proxy.
    const worksheets = workbook.worksheets; // worksheet collection proxy.

    // Create/reuse a stable temporary sheet to anchor "insert before" positioning.
    let tempSheet = worksheets.getItemOrNullObject(TEMP_SHEET_NAME);
    tempSheet.load("name");
    await context.sync();
    if ((tempSheet as any).isNullObject) {
      tempSheet = worksheets.add(TEMP_SHEET_NAME);
      tempSheet.load("name");
      await context.sync();
    }

    // IMPORTANT:
    // `_SS_META` is used by the task pane to resolve PDF hyperlinks.
    // If an old `_SS_META` remains, inserting a new report may cause Excel to rename the incoming `_SS_META`
    // sheet (e.g. "_SS_META2"), which breaks lookups. Therefore we always remove any existing `_SS_META`
    // before inserting the new workbook.
    try {
      const metaSheet = worksheets.getItemOrNullObject(META_SHEET_NAME);
      metaSheet.load(["name", "visibility"]);
      await context.sync();
      if (!(metaSheet as any).isNullObject) {
        try {
          if ((metaSheet as Excel.Worksheet).visibility !== Excel.SheetVisibility.visible) {
            (metaSheet as Excel.Worksheet).visibility = Excel.SheetVisibility.visible;
            await context.sync();
          }
        } catch {
          // Best-effort: still attempt deletion.
        }
        metaSheet.delete();
        await context.sync();
      }
    } catch (metaDeleteError) {
      // If this fails we still attempt to insert the workbook, but PDF metadata may be stale.

      console.warn("Failed to delete existing _SS_META before inserting report.", metaDeleteError);
    }

    // Snapshot names pre-insert so we can identify inserted sheets.
    worksheets.load("items/name");
    await context.sync();
    const baselineNames = new Set<string>(worksheets.items.map((sheet) => sheet.name));

    workbook.insertWorksheetsFromBase64(base64, {
      positionType: Excel.WorksheetPositionType.before,
      relativeTo: tempSheet,
    });
    await context.sync();

    worksheets.load("items/name");
    await context.sync();
    const afterNames = worksheets.items.map((sheet) => sheet.name);
    const insertedNames = afterNames.filter(
      (name) => !baselineNames.has(name) && name !== tempSheet.name
    );
    if (insertedNames.length === 0) {
      throw new Error("Report download succeeded but no new worksheets were inserted into Excel.");
    }

    return {
      tempSheetName: tempSheet.name,
      insertedNames,
    };
  });

  // Phase 2 (best-effort): cleanup prior sheets, rename, set gridlines, and write metadata.
  // If this fails, the user still has the report inserted; we should not show a scary error.
  try {
    await Excel.run(async (context) => {
      const workbook = context.workbook;
      const worksheets = workbook.worksheets;
      worksheets.load("items/name");
      await context.sync();

      const currentNames = worksheets.items.map((sheet) => sheet.name);

      // 2a) Always clean stale SS_META sheets that are not part of the newly inserted workbook.
      // This is independent of cached metadata and prevents sheet-name drift like "_SS_META2".
      const staleMetaSheets = currentNames
        .filter((name) => name.startsWith(META_SHEET_NAME))
        .filter((name) => !insertResult.insertedNames.includes(name))
        .filter((name) => name !== insertResult.tempSheetName);

      for (const name of staleMetaSheets) {
        const sheet = worksheets.getItemOrNullObject(name);
        sheet.load(["visibility"]);
        await context.sync();
        if ((sheet as any).isNullObject) {
          continue;
        }
        try {
          if ((sheet as Excel.Worksheet).visibility !== Excel.SheetVisibility.visible) {
            (sheet as Excel.Worksheet).visibility = Excel.SheetVisibility.visible;
            await context.sync();
          }
        } catch {
          // best-effort
        }
        try {
          sheet.delete();
          await context.sync();
        } catch (metaCleanupError) {
          console.warn("Failed to delete stale SS_META sheet during cleanup.", metaCleanupError);
        }
      }

      // 2b) Delete old managed sheets (if present) but never delete the newly inserted sheets.
      const toDelete = snapshot.managedSheetNames
        .filter((name) => name !== insertResult.tempSheetName)
        .filter((name) => !insertResult.insertedNames.includes(name));

      for (const name of toDelete) {
        const sheet = worksheets.getItemOrNullObject(name);
        sheet.load(["visibility"]);
        await context.sync();
        if ((sheet as any).isNullObject) {
          continue;
        }
        try {
          if ((sheet as Excel.Worksheet).visibility !== Excel.SheetVisibility.visible) {
            (sheet as Excel.Worksheet).visibility = Excel.SheetVisibility.visible;
            await context.sync();
          }
        } catch {
          // visibility fix is best-effort; continue to delete attempt.
        }

        try {
          sheet.delete();
          await context.sync();
        } catch (deleteError) {
          console.warn("Failed to delete a prior managed sheet.", { sheetName: name, deleteError });
        }
      }

      // Refresh names after deletions.
      worksheets.load("items/name");
      await context.sync();
      const namesAfterDelete = worksheets.items.map((sheet) => sheet.name);
      const renameReserved = new Set<string>(namesAfterDelete);

      // 2c) Rename inserted sheets (best-effort). Errors typically surface on context.sync(),
      // so we sync per-rename to isolate failures.
      const finalNames: string[] = [];
      for (const insertedName of insertResult.insertedNames) {
        let finalName = insertedName;
        if (!shouldPreserveSheetName(insertedName)) {
          try {
            const candidate = buildSheetNameWithJobId(insertedName, job.id, renameReserved);
            const sheet = worksheets.getItemOrNullObject(insertedName);
            sheet.load("name");
            await context.sync();
            if (!(sheet as any).isNullObject) {
              sheet.name = candidate;
              await context.sync();
              finalName = candidate;
            }
          } catch (renameError) {
            console.warn("Failed to rename an inserted worksheet.", { insertedName, renameError });
            finalName = insertedName;
          }
        }
        renameReserved.add(finalName);
        finalNames.push(finalName);
      }

      // 2c.1) Fallback cleanup:
      // If workbook sheet metadata is missing/empty (common when metadata persistence previously failed),
      // delete any prior report sheets we can identify by `_ST_` prefix. Never delete the newly inserted sheets.
      if (!snapshot.managedSheetNames || snapshot.managedSheetNames.length === 0) {
        worksheets.load("items/name");
        await context.sync();
        const allNamesNow = worksheets.items.map((s) => s.name);
        const fallbackDelete = allNamesNow
          .filter((name) => name.startsWith(ST_SHEET_PREFIX))
          .filter((name) => !finalNames.includes(name))
          .filter((name) => name !== insertResult.tempSheetName);

        for (const name of fallbackDelete) {
          try {
            const sheet = worksheets.getItemOrNullObject(name);
            sheet.load(["visibility"]);
            await context.sync();
            if ((sheet as any).isNullObject) {
              continue;
            }
            try {
              if ((sheet as Excel.Worksheet).visibility !== Excel.SheetVisibility.visible) {
                (sheet as Excel.Worksheet).visibility = Excel.SheetVisibility.visible;
                await context.sync();
              }
            } catch {
              // best-effort
            }
            sheet.delete();
            await context.sync();
          } catch (fallbackDeleteError) {
            console.warn("Failed to delete a prior _ST_ report sheet (fallback).", {
              sheetName: name,
              fallbackDeleteError,
            });
          }
        }
      }

      // 2d) Delete temporary sheet (best-effort).
      try {
        const temp = worksheets.getItemOrNullObject(insertResult.tempSheetName);
        temp.load("name");
        await context.sync();
        if (!(temp as any).isNullObject) {
          temp.delete();
          await context.sync();
        }
      } catch (tempDeleteError) {
        console.warn("Failed to delete temporary worksheet.", tempDeleteError);
      }

      // 2e) Persist workbook metadata (best-effort).
      try {
        await persistWorkbookMetadata(context, workbook, job.id, finalNames);
      } catch (metaError: any) {
        console.warn(
          "Failed to persist workbook metadata for reloads. code=%s message=%s",
          metaError?.code ?? "unknown",
          metaError?.message ?? String(metaError),
          metaError
        );
      }

      // 2f) Cosmetic: gridlines off for all inserted report sheets (best-effort).
      for (const name of finalNames) {
        try {
          const s = worksheets.getItemOrNullObject(name);
          s.load("name");
          await context.sync();
          if (!(s as any).isNullObject) {
            (s as Excel.Worksheet).showGridlines = false;
            await context.sync();
          }
        } catch (gridError) {
          console.warn("Failed to set showGridlines=false for a report sheet.", {
            sheetName: name,
            gridError,
          });
        }
      }
    });
  } catch (postInsertError) {
    // Critical UX decision: the report is already inserted. Do not fail the operation.
    // Keep a minimal console warning so devs can still inspect details when needed.

    console.warn("Non-critical post-insert Excel step failed after report load.", postInsertError);
  }
};

export const DashboardPage: React.FC<{ onNavigate: (page: Page) => void }> = ({ onNavigate }) => {
  const styles = useStyles();
  const { state, dispatch } = useJob();
  const { instance } = useMsal();
  const { currentBatchId } = state;

  const [jobDetails, setJobDetails] = useState<JobDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [jobs, setJobs] = useState<DashboardJob[]>([]);
  const [visibleCount, setVisibleCount] = useState<number>(10);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [pendingReload, setPendingReload] = useState<PendingReloadState | null>(null);
  const [isReloadDialogOpen, setIsReloadDialogOpen] = useState(false);
  const [expandedRejectedByJobId, setExpandedRejectedByJobId] = useState<Record<string, boolean>>(
    {}
  );

  // Sort by submission time (created_at) so the order remains stable as jobs progress.
  const sortedJobs = useMemo(() => {
    return Array.isArray(jobs)
      ? [...jobs].sort(
          (a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
        )
      : [];
  }, [jobs]);

  const enrichedJobs = useMemo(() => {
    if (!jobDetails) {
      return sortedJobs;
    }
    return sortedJobs.map((job) => {
      if (job.id !== currentBatchId) {
        return job;
      }
      return {
        ...job,
        status: jobDetails.status,
        progress_percent: jobDetails.progress_percent ?? job.progress_percent,
        final_report_drive_id: jobDetails.final_report_drive_id ?? job.final_report_drive_id,
        final_report_item_id: jobDetails.final_report_item_id ?? job.final_report_item_id,
        error_message: jobDetails.error_message ?? job.error_message,
        rejected_file_count: jobDetails.rejected_file_count ?? job.rejected_file_count,
        rejected_files: jobDetails.rejected_files ?? job.rejected_files,
      };
    });
  }, [sortedJobs, jobDetails, currentBatchId]);

  const toggleRejectedFiles = (jobId: string) => {
    setExpandedRejectedByJobId((prev) => ({
      ...prev,
      [jobId]: !prev[jobId],
    }));
  };

  useEffect(() => {
    let stopped = false;
    // Poll cadence cheat sheet:
    // - Start at 2 s to surface fresh updates quickly after mounting.
    // - Successful active poll → 15 s cadence (a job's progress changes minutes apart).
    // - Terminal jobs (completed/failed) → 60 s cadence because status is unlikely to change.
    // - No jobs present → 60 s cadence to avoid needless load.
    // - Errors → exponential backoff capped at 60 s to protect the backend.
    // - Tab visibility change → immediate poll so the dashboard feels responsive.
    let backoffMs = 2000; // Initial 2 s delay: fast feedback when the dashboard first mounts.
    const maxBackoffMs = 60000; // Safety cap: never back off beyond 60 s even after repeated errors.
    let timer: ReturnType<typeof setTimeout>;

    const scheduleNext = () => {
      if (stopped) return;
      clearTimeout(timer);
      // Apply a ±400 ms jitter so multiple clients do not synchronize their polls.
      timer = setTimeout(tick, backoffMs + Math.floor(Math.random() * 400));
    };

    const immediate = () => {
      if (stopped) return;
      clearTimeout(timer);
      setTimeout(tick, 0); // Zero-delay poll when visibility or manual triggers demand instant refresh.
    };

    const tick = async () => {
      if (stopped) return;
      try {
        const accessToken = await getAccessToken(instance);

        // Always refresh recent jobs list for display
        const recent = await listJobs(accessToken, 50); // Lists top 50 jobs on every cycle regardless of cadence.
        const normalized = Array.isArray(recent) ? recent : [];
        setJobs(normalized);

        // Rehydrate if no current batch: pick freshest
        let batchId = currentBatchId;
        if (!batchId) {
          const sorted = [...normalized].sort(
            (a: DashboardJob, b: DashboardJob) =>
              new Date(b.updated_at || b.created_at || 0).getTime() -
              new Date(a.updated_at || a.created_at || 0).getTime()
          );
          const first = sorted.length > 0 ? sorted[0] : null;
          if (first && first.id) {
            batchId = first.id;
          }
        }

        if (batchId) {
          const status = await getJobStatus(batchId!, accessToken);
          setJobDetails(status);
          setError(null);
          setIsLoading(false);
          setIsReconnecting(false);

          // Successful poll with an active job: poll every 15s.
          backoffMs = 15000;

          if (status.status === "completed" || status.status === "failed") {
            // Terminal job: slow down to 60s to reduce noise when nothing else is happening.
            backoffMs = 60000;
          }
        } else {
          // No jobs
          setJobDetails(null);
          setIsLoading(false);
          setIsReconnecting(false);
          backoffMs = 60000; // Empty dashboard: 60s cadence keeps the UI fresh without hammering the API.
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Network issue while checking status.");
        setIsReconnecting(true);
        setIsLoading(false);
        // Error path: exponential backoff (cap at 60 s) to give the backend breathing room.
        backoffMs = Math.min(maxBackoffMs, Math.max(2000, Math.floor(backoffMs * 2)));
      } finally {
        scheduleNext();
      }
    };

    // Visibility-driven immediate refresh
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        backoffMs = 0; // Foregrounding the tab: reset delay so we refresh right away.
        immediate(); // Kick an immediate poll to show up-to-date progress as soon as the user returns.
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    // Kick off immediately
    immediate();

    return () => {
      stopped = true;
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [currentBatchId, instance]);

  const handleStartNewJob = () => {
    dispatch({ type: "RESET_JOB" });
    onNavigate("taskSelection");
  };

  const loadAndShowReport = async (
    base64Loader: () => Promise<string>,
    job: CompletedJobSummary,
    options?: { snapshot?: WorkbookSnapshot }
  ): Promise<boolean> => {
    try {
      const snapshot = options?.snapshot ?? (await readWorkbookSnapshot());
      setIsGeneratingReport(true);
      const base64 = await base64Loader();
      await applyReportToWorkbook(base64, job, snapshot);
      window.SS_invalidateMetaIndex?.();
      setError(null);
      return true;
    } catch (err) {
      console.error(
        "View report failed",
        err,
        (err as any)?.debugInfo,
        (err as any)?.traceMessages
      );
      setError(
        err instanceof Error
          ? err.message
          : "An unknown error occurred while generating the report."
      );
      return false;
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const queueReportLoad = async (
    job: CompletedJobSummary,
    base64Loader: () => Promise<string>,
    onLoaded?: () => void
  ) => {
    const snapshot = await readWorkbookSnapshot();
    setPendingReload({ job, base64Loader, snapshot, onLoaded });
    setIsReloadDialogOpen(true);
  };

  const handleViewReport = async (job: CompletedJobSummary) => {
    if (!job.final_report_drive_id || !job.final_report_item_id) {
      setError("Unable to locate the final report for this job.");
      return;
    }

    const base64Loader = async () => {
      const backendToken = await getAccessToken(instance);
      return downloadFileBase64ByIds(
        job.final_report_drive_id!,
        job.final_report_item_id!,
        backendToken
      );
    };

    await queueReportLoad(job, base64Loader);
  };

  // Removed "View & go to report options": the dashboard only needs to open the report.

  const handleReloadCancel = () => {
    setIsReloadDialogOpen(false);
    setPendingReload(null);
  };

  const handleReloadConfirm = async () => {
    if (!pendingReload) {
      return;
    }
    setIsReloadDialogOpen(false);
    const { job, base64Loader, snapshot, onLoaded } = pendingReload;
    setPendingReload(null);
    const succeeded = await loadAndShowReport(base64Loader, job, { snapshot });
    if (succeeded) {
      onLoaded?.();
    }
  };

  const renderReloadDialog = () => (
    <Dialog
      open={isReloadDialogOpen}
      onOpenChange={(_, data) => {
        if (!data.open) {
          handleReloadCancel();
        }
      }}
    >
      <DialogSurface className={styles.dialogSurface}>
        <DialogBody>
          <DialogTitle className={styles.dialogTitle}>Replace current sheets?</DialogTitle>
          <DialogContent className={styles.dialogContent}>
            Loading this report will remove the sheets currently inserted by the add-in. Do you want
            to continue?
          </DialogContent>
          <DialogActions>
            <Button
              appearance="secondary"
              className={styles.backButton}
              onClick={handleReloadCancel}
              disabled={isGeneratingReport}
            >
              Keep current sheets
            </Button>
            <Button
              appearance="primary"
              onClick={handleReloadConfirm}
              disabled={isGeneratingReport}
            >
              {isGeneratingReport ? "Loading…" : "Load report"}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );

  const renderJobCard = (job: DashboardJob) => {
    const status: JobStatus | string = job.status || "unknown";
    // Show the time the job was originally submitted (created_at), not the last-updated
    // time, so the timestamp stays stable as the job progresses through processing.
    const submittedAt = new Date(job.created_at || Date.now());
    const progress = job.progress_percent
      ? job.progress_percent / 100
      : status === "completed"
        ? 1
        : 0;
    const isCompleted = status === "completed";
    const isFailed = status === "failed";
    const isActive = job.id === currentBatchId;
    const rejectedFiles: RejectedFileInfo[] = Array.isArray(job.rejected_files)
      ? job.rejected_files
      : [];
    const rejectedFileCount =
      typeof job.rejected_file_count === "number" ? job.rejected_file_count : rejectedFiles.length;
    const hasRejectedFiles = rejectedFileCount > 0;
    const isRejectedListExpanded = Boolean(expandedRejectedByJobId[job.id]);
    const rejectedSummaryPrefix = isCompleted
      ? "Completed with warnings"
      : "Skipped files during processing";
    const theme = getJobTheme(status);
    const themeClasses = {
      active: {
        badge: styles.activeBadge,
        progress: styles.activeProgress,
      },
      completed: {
        badge: styles.completedBadge,
        progress: styles.completedProgress,
      },
      failed: {
        badge: styles.failedBadge,
        progress: styles.failedProgress,
      },
    }[theme];
    const progressPercentage = Math.min(100, Math.max(0, progress * 100));
    const statusLabel = String(status || "Unknown").replace(/_/g, " ");

    return (
      <Card
        key={job.id}
        role="button"
        tabIndex={0}
        aria-label={`Select ${job.job_type?.replace(/_/g, " ") || "job"}`}
        className={mergeClasses(styles.jobCard, isActive && styles.jobCardActive)}
        onClick={() => dispatch({ type: "SET_CURRENT_BATCH_ID", payload: job.id })}
        onKeyDown={(event) => {
          if (shouldActivateJobCard(event.key, event.target, event.currentTarget)) {
            event.preventDefault();
            dispatch({ type: "SET_CURRENT_BATCH_ID", payload: job.id });
          }
        }}
      >
        <div className={styles.jobHeader}>
          <div className={styles.jobHeading}>
            <h3 className={styles.jobTitle}>{job.job_type?.replace(/_/g, " ") || "Job"}</h3>
            <p className={styles.jobId} title={job.id}>
              Job ID: {job.id}
            </p>
          </div>
          <Badge
            appearance="outline"
            className={mergeClasses(styles.statusBadge, themeClasses.badge)}
            shape="rounded"
            size="small"
          >
            {statusLabel}
          </Badge>
        </div>

        <div className={styles.jobBody}>
          {hasRejectedFiles && (
            <div className={styles.warningRow}>
              <span className={styles.warningText}>
                {rejectedSummaryPrefix}: {rejectedFileCount} file
                {rejectedFileCount === 1 ? "" : "s"} skipped.
              </span>
              <Button
                appearance="subtle"
                className={styles.warningButton}
                icon={isRejectedListExpanded ? <ChevronUp16Regular /> : <ChevronDown16Regular />}
                iconPosition="after"
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  toggleRejectedFiles(job.id);
                }}
              >
                {isRejectedListExpanded ? "Hide details" : "View details"}
              </Button>
            </div>
          )}
          {hasRejectedFiles && isRejectedListExpanded && (
            <div className={styles.rejectedList}>
              {rejectedFiles.map((file, index) => (
                <p key={`${job.id}-${file.file_name}-${index}`} className={styles.rejectedItem}>
                  <strong>{file.file_name}:</strong> {file.reason}
                </p>
              ))}
            </div>
          )}
          <div
            className={styles.progressTrack}
            role="progressbar"
            aria-label={`${statusLabel} progress`}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progressPercentage)}
          >
            <div
              className={mergeClasses(styles.progressBar, themeClasses.progress)}
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>

        <div className={styles.jobActions}>
          <div className={styles.actionMeta}>
            <div className={styles.submittedRow}>
              <Clock16Regular aria-hidden="true" />
              <span>{submittedAt.toLocaleDateString()}</span>
              <span className={styles.submittedTime}>{submittedAt.toLocaleTimeString()}</span>
            </div>
            {isFailed && job.error_message && (
              <p className={styles.errorText}>{job.error_message}</p>
            )}
          </div>
          <div>
            {isCompleted && job.final_report_drive_id && job.final_report_item_id && (
              <Button
                appearance="primary"
                className="sourcestone-navigation-primary"
                icon={isGeneratingReport ? <Spinner size="tiny" /> : <DocumentTable24Regular />}
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewReport({
                    id: job.id,
                    job_type: job.job_type,
                    final_report_drive_id: job.final_report_drive_id,
                    final_report_item_id: job.final_report_item_id,
                  });
                }}
                disabled={isGeneratingReport}
              >
                {isGeneratingReport ? "Opening..." : "View Report"}
              </Button>
            )}
          </div>
        </div>
      </Card>
    );
  };

  return (
    <div className={styles.root}>
      {renderReloadDialog()}
      <Card className={mergeClasses(styles.surface, styles.headerCard)}>
        <div className={styles.headerRow}>
          <div className={styles.headerIcon}>
            <Briefcase24Regular aria-hidden="true" />
          </div>
          <div>
            <p className={styles.eyebrow}>Workspace</p>
            <h1 className={styles.pageTitle}>Jobs Dashboard</h1>
            <p className={styles.pageDescription}>
              Monitor recent jobs and open completed reports directly in Excel.
            </p>
          </div>
        </div>
        {error && !isReconnecting && (
          <MessageBar className={styles.errorMessage} intent="error">
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        )}
      </Card>

      <div className={styles.listContainer}>
        <Card className={mergeClasses(styles.surface, styles.jobsCard)}>
          <div className={styles.listHeader}>
            <div>
              <h2 className={styles.listTitle}>Recent jobs</h2>
              <p className={styles.listDescription}>Latest submissions and report status</p>
            </div>
            {!isLoading && enrichedJobs.length > 0 && (
              <Badge
                appearance="outline"
                className={styles.totalBadge}
                shape="rounded"
                size="small"
              >
                {enrichedJobs.length} total
              </Badge>
            )}
          </div>
          {isLoading && enrichedJobs.length === 0 ? (
            <div className={styles.loadingState}>
              <Spinner label="Loading jobs..." size="small" />
            </div>
          ) : enrichedJobs.length > 0 ? (
            <div className={styles.jobsScrollArea}>
              {enrichedJobs.slice(0, visibleCount).map(renderJobCard)}
              {visibleCount < enrichedJobs.length && (
                <div className={styles.viewMoreRow}>
                  <Button
                    appearance="secondary"
                    className={styles.secondaryButton}
                    size="small"
                    onClick={() => setVisibleCount((count) => count + 10)}
                  >
                    View more
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>
                <Briefcase24Regular aria-hidden="true" />
              </div>
              <p className={styles.emptyTitle}>No recent jobs</p>
              <p className={styles.emptyDescription}>
                Start a new job and its progress will appear here.
              </p>
            </div>
          )}
        </Card>
      </div>

      <Card className={mergeClasses(styles.surface, styles.footerCard)}>
        <div className={styles.footerActions}>
          <Button
            appearance="primary"
            className={mergeClasses(
              styles.footerButton,
              styles.backButton,
              "sourcestone-navigation-primary"
            )}
            icon={<ArrowLeft24Regular />}
            onClick={() => onNavigate("taskSelection")}
            disabled={isGeneratingReport}
          >
            Back to Task Selection
          </Button>
          <Button
            appearance="primary"
            className={mergeClasses(styles.footerButton, "sourcestone-navigation-primary")}
            icon={<Add24Regular />}
            onClick={handleStartNewJob}
            disabled={isGeneratingReport}
          >
            Start New Job
          </Button>
        </div>
      </Card>
    </div>
  );
};
